import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent } from 'react'
import { t } from '@stokr/components-library'
import { PriceChart, chartColor, formatChartCompact, toneColor, type ChartHover } from 'components/ui/chart'
import { CHART_HEIGHT, ChartFootnote, ChartIdentity, MinedSourceNote, RangePicker } from 'components/sto/chart'
import { useChartRange } from 'hooks/sto/useChartRange'
import { useChartDenomination, useProductChart } from 'hooks/sto/useProductChart'
import { useStoProject } from 'hooks/sto/useStoProject'
import { ProjectTypes } from 'constants/enums'
import { Identity, IdentityText, SymbolLine } from 'components/sto/chart/chart.styles'
import { latestDate, redeemedOn } from 'models/productChart'
import { chartReadout } from 'models/chart'
import {
  Body,
  BodyClip,
  BodyInner,
  Card,
  CardFootnote,
  ChartFadeIn,
  LabelSkeleton,
  Header,
  HeaderSlot,
  Root,
  Sparkline,
  Swap,
  ToggleButton,
  ValueSkeleton,
} from './HeroChart.styles'
import { useChartHoverTracking, useStoTrack } from 'hooks/sto/useStoTracking'
import { useIsBelowDesktop } from 'hooks/sto/useIsBelowDesktop'

const SPARKLINE_HEIGHT = 68
/** The product types with a chart (useProductChart): funds, equity notes and structured notes. */
const CHART_TYPES: ReadonlySet<string> = new Set([ProjectTypes.FUND, ProjectTypes.EQUITY, ProjectTypes.TRANCHES])
const NO_POINTS: never[] = []

/** Material "unfold more / less" — the Figma expand and collapse glyphs. */
const UNFOLD_MORE =
  'M12 5.83 15.17 9l1.41-1.41L12 3 7.41 7.59 8.83 9 12 5.83zm0 12.34L8.83 15l-1.41 1.41L12 21l4.59-4.59L15.17 15 12 18.17z'
const UNFOLD_LESS =
  'M7.41 18.59 8.83 20 12 16.83 15.17 20l1.41-1.41L12 14l-4.59 4.59zm9.18-13.18L15.17 4 12 7.17 8.83 4 7.41 5.41 12 10l4.59-4.59z'

/** The card widens from half to full width this long (HeroChart.styles DURATION). */
const OPEN_MS = 360

/**
 * The card while its data loads: the symbol, known from the project, and the label and value
 * held at their sizes — so the card is there at once and nothing moves when they arrive.
 */
const HeroChartSkeleton = ({ symbol }: { symbol: string }) => (
  <Root aria-busy="true">
    <Card $open={false}>
      <Header>
        <Identity>
          <IdentityText>
            <SymbolLine>
              {symbol && <strong>{symbol} - </strong>}
              <LabelSkeleton as="span" $height={12} />
            </SymbolLine>
            <ValueSkeleton $height={20} />
          </IdentityText>
        </Identity>
      </Header>
    </Card>
  </Root>
)

/**
 * The product's price card under the hero metrics: a summary with a sparkline that expands
 * into the full chart. While hovering, the value and change follow the cursor.
 */
export const HeroChart = () => {
  const state = useProductChart()
  const { project } = useStoProject()
  const bodyId = useId()
  const isBelowDesktop = useIsBelowDesktop()
  const [isOpen, setIsOpen] = useState(false)
  // The full chart is drawn once the card has finished widening: drawn mid-transition it lays
  // out at half width and visibly jumps. Until then a placeholder holds its height.
  const [isSettled, setIsSettled] = useState(false)
  const [hover, setHover] = useState<ChartHover | null>(null)
  // Bumped each time the sparkline reappears after closing, so its line draws in again.
  const [sparklineDraw, setSparklineDraw] = useState(0)
  const wasOpen = useRef(false)
  const trackSto = useStoTrack()
  const trackHover = useChartHoverTracking('hero')
  const onHover = useCallback(
    (next: ChartHover | null) => {
      setHover(next)
      trackHover(next)
    },
    [trackHover],
  )

  // Closing keeps the chart until the card has shrunk, so it fades out with the card.
  useEffect(() => {
    const closing = wasOpen.current && !isOpen
    wasOpen.current = isOpen
    const timer = window.setTimeout(() => {
      setIsSettled(isOpen)
      if (closing) setSparklineDraw((count) => count + 1)
    }, OPEN_MS)
    return () => window.clearTimeout(timer)
  }, [isOpen])

  const view = state.status === 'ready' ? state.data : undefined
  const chart = view?.chart
  // The currency picked by the hero title, else the chart's default.
  const series = useChartDenomination(chart)
  // A running mining note (BMN2) headlines its issuance amount rather than the BTC mined.
  const issuance = chart?.note?.issuance?.length ? chart.note.issuance : undefined
  // A fund headlines its total NAV (unit NAV × units) in the picked currency; without its units, the unit NAV.
  const fundNav = chart?.fundNavs?.find((nav) => nav.currency === series?.currency && nav.points.length > 0)?.points
  const headline = issuance ?? fundNav
  const range = useChartRange(headline ?? series?.points ?? NO_POINTS, undefined, () => setHover(null))
  const { points } = range

  // A redeemed note with only its issuance to show (PKB2): a flat zero says nothing up here, so its
  // history stays in Performance. One that mined BTC (BMN1) keeps its chart.
  const retired = Boolean(chart && redeemedOn(chart) !== undefined && chart.denominations[0].points.length === 0)

  // Loading, the card holds its place (only products that have a chart: a company STO has none, so it
  // must not flash one); no chart at all, nothing.
  if (!view || !chart || !series || points.length === 0 || retired) {
    const hasChart = CHART_TYPES.has(project?.type ?? '')
    return state.status === 'loading' && hasChart ? (
      <HeroChartSkeleton
        // The symbol the chart will show: an equity note's is its underlying's.
        symbol={(project?.type === ProjectTypes.EQUITY ? project.underlyingSymbol : project?.tokenSymbol)?.trim() ?? ''}
      />
    ) : null
  }

  const readout = chartReadout(points, hover?.value)
  // Funds and mining notes in brand blue; market prices green when up, red when down.
  const lineColor = chart.kind === 'stock' ? toneColor(readout.rangeTone) : chartColor.fund
  const labelKey = issuance ? 'stoPage.heroChart.issuance' : fundNav ? 'stoPage.heroChart.fundNav' : chart.labelKey

  // Gold and fiat dates can differ: the date of the currency shown.
  const footnote = <ChartFootnote asOf={latestDate(headline ?? series.points) ?? chart.asOf} source={chart.source} />

  const toggle = (via: 'row' | 'button') => {
    trackSto('sto_chart_toggled', { chart: 'hero', open: !isOpen, via })
    setIsOpen((open) => !open)
    setHover(null)
  }

  return (
    <Root>
      <Card $open={isOpen}>
        {/* Collapsed, the whole row opens the chart (a mouse shortcut: the button stays the control).
            Open, the row holds the range tabs on desktop, so only the button closes it; mobile has
            no button, and the row both opens and closes it. */}
        <Header
          $clickable={!isOpen || isBelowDesktop}
          data-cy="sto-hero-chart-header"
          onClick={isOpen && !isBelowDesktop ? undefined : () => toggle('row')}
          // Mobile hides the button: the row is the control, for the keyboard and screen readers too.
          {...(isBelowDesktop && {
            role: 'button',
            tabIndex: 0,
            'aria-expanded': isOpen,
            'aria-controls': bodyId,
            'aria-label': t(isOpen ? 'stoPage.heroChart.collapse' : 'stoPage.heroChart.expand'),
            onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => {
              if (event.key !== 'Enter' && event.key !== ' ') return
              event.preventDefault()
              toggle('row')
            },
          })}>
          <ChartIdentity
            icon={chart.icon}
            symbol={chart.symbol}
            labelKey={labelKey}
            symbolInLabel={labelKey === 'stoPage.heroChart.minedBtcPerNote'}
            // BMN2 charts its BTC mined in BTC; an issuance headline is in the note's currency.
            currency={issuance ? (chart.note?.issuanceCurrency ?? series.currency) : series.currency}
            // The change only for a market price (equity): a fund's NAV, an issuance amount or BTC mined are
            // totals, whose change mixes subscriptions, redemptions, issuance and the BTC price, and reads
            // as a return it is not.
            readout={chart.kind === 'stock' ? readout : { ...readout, percent: undefined }}
            // Open, the range tabs say it.
            range={isOpen ? undefined : range.active}
            // Collapsed, a big value is shortened; open, it is shown in full.
            short={!isOpen}
          />

          {/* The selected range, as the figures beside it. After closing it waits for the card to
              finish shrinking: shown at once, it would redraw at every width on the way. */}
          <Sparkline $visible={!isOpen && !isSettled} aria-hidden="true">
            {points.length > 1 && (
              <PriceChart
                // A new chart per range and per reappearance: each draws its line in from the left.
                key={`${range.active}-${sparklineDraw}`}
                points={points}
                color={lineColor}
                height={SPARKLINE_HEIGHT}
                interactive={false}
                drawIn
              />
            )}
          </Sparkline>

          <HeaderSlot $open={isOpen}>
            <Swap $visible={isOpen} inert={!isOpen}>
              <RangePicker range={range} chart="hero" placement="header" />
            </Swap>
          </HeaderSlot>

          <ToggleButton
            type="button"
            aria-expanded={isOpen}
            aria-controls={bodyId}
            aria-label={t(isOpen ? 'stoPage.heroChart.collapse' : 'stoPage.heroChart.expand')}
            data-cy="sto-hero-chart-toggle"
            onClick={(event) => {
              // The row's own click would toggle it straight back.
              event.stopPropagation()
              toggle('button')
            }}>
            <svg width={20} height={20} viewBox="0 0 24 24" aria-hidden="true">
              <path d={isOpen ? UNFOLD_LESS : UNFOLD_MORE} fill="currentColor" />
            </svg>
          </ToggleButton>
        </Header>

        <Body id={bodyId} $open={isOpen} inert={!isOpen}>
          <BodyClip>
            <BodyInner>
              {isSettled ? (
                <ChartFadeIn>
                  <PriceChart
                    // A new chart per range: the line draws in from the left instead of jumping.
                    key={range.active}
                    points={points}
                    color={lineColor}
                    height={CHART_HEIGHT}
                    showAxes
                    drawIn
                    denseValueAxis={!issuance && series.currency === 'BTC'}
                    formatValue={formatChartCompact}
                    onHover={onHover}
                    description={t('stoPage.heroChart.description', { symbol: chart.symbol || t(labelKey) })}
                  />
                </ChartFadeIn>
              ) : (
                <div style={{ height: CHART_HEIGHT }} aria-hidden="true" />
              )}
              <RangePicker range={range} chart="hero" placement="below" />
            </BodyInner>
          </BodyClip>
        </Body>
        {/* Only open: the date and source go with the full chart. */}
        {isOpen && (
          <CardFootnote>
            {footnote}
            {chart.symbol === 'BMN2' && labelKey === 'stoPage.heroChart.minedBtcPerNote' && <MinedSourceNote />}
          </CardFootnote>
        )}
      </Card>
    </Root>
  )
}
